import dayjs from "dayjs";
import { EVENT_COLORS, ROADMAP_INDICATOR_COLOR } from "@/pages/roadmap/constants";
import type { CellBar, DayCell, EventStatus, GameEvent } from "@/pages/roadmap/types";

export function createDayjsDate(year: number, month: number, day: number) {
  return dayjs(new Date(year, month, day));
}

export function toKey(year: number, month: number, day: number) {
  return createDayjsDate(year, month, day).format("YYYY-MM-DD");
}

export function getEventsOn(events: GameEvent[], year: number, month: number, day: number) {
  const key = toKey(year, month, day);
  return events.filter((event) => event.start <= key && key <= event.end);
}

export function fmtRange(event: GameEvent) {
  return event.start === event.end
    ? dayjs(event.start).format("YYYY.MM.DD")
    : `${dayjs(event.start).format("YYYY.MM.DD")} ~ ${dayjs(event.end).format("YYYY.MM.DD")}`;
}

export function getNearestUpcoming(events: GameEvent[], year: number, month: number, day: number): GameEvent | null {
  const key = toKey(year, month, day);
  return events.filter((event) => event.end >= key).sort((a, b) => a.start.localeCompare(b.start))[0] ?? null;
}

export function getStatus(event: GameEvent, todayKey: string): EventStatus {
  if (todayKey > event.end) return "completed";
  if (todayKey >= event.start) return "ongoing";
  return "upcoming";
}

export function daysUntil(event: GameEvent, todayKey: string) {
  return dayjs(event.start).diff(dayjs(todayKey), "day");
}

export function durationDays(event: GameEvent) {
  return dayjs(event.end).diff(dayjs(event.start), "day") + 1;
}

export function diffDays(fromKey: string, toKeyValue: string) {
  return dayjs(toKeyValue).diff(dayjs(fromKey), "day");
}

export function buildGrid(year: number, month: number): DayCell[] {
  const firstOfMonth = createDayjsDate(year, month, 1);
  const firstDay = firstOfMonth.day();
  const daysInMonth = firstOfMonth.daysInMonth();
  const daysInPrevMonth = firstOfMonth.subtract(1, "month").daysInMonth();
  const cells: DayCell[] = [];

  for (let index = firstDay - 1; index >= 0; index--) {
    cells.push({ day: daysInPrevMonth - index, kind: "prev" });
  }

  for (let day = 1; day <= daysInMonth; day++) {
    cells.push({ day, kind: "cur" });
  }

  const remainder = cells.length % 7;
  if (remainder > 0) {
    for (let day = 1; day <= 7 - remainder; day++) {
      cells.push({ day, kind: "next" });
    }
  }

  return cells;
}

export function buildCalendarRows(year: number, month: number) {
  const grid = buildGrid(year, month);
  const rows: DayCell[][] = [];

  for (let index = 0; index < grid.length; index += 7) {
    rows.push(grid.slice(index, index + 7));
  }

  return rows;
}

/**
 * 특정 셀(날짜)의 실제 [연, 월, 일] 정보를 반환합니다.
 */
export function getCellDate(cell: DayCell, viewYear: number, viewMonth: number): [number, number, number] {
  let y = viewYear, m = viewMonth;
  if (cell.kind === "prev") { m -= 1; if (m < 0) { m = 11; y -= 1; } }
  if (cell.kind === "next") { m += 1; if (m > 11) { m = 0; y += 1; } }
  return [y, m, cell.day];
}

/**
 * 특정 날짜에 걸쳐 있는 이벤트들을 CellBar 형식으로 반환합니다.
 */
export function getCellBarsForDate(events: GameEvent[], year: number, month: number, day: number): CellBar[] {
  const key = toKey(year, month, day);
  const todayKey = dayjs().format("YYYY-MM-DD");

  return events.filter((event) => event.start <= key && key <= event.end).map((event) => {
    const isStart = event.start === key;
    const isEnd = event.end === key;
    const isPast = todayKey > event.end;

    const globalIndex = events.findIndex(e => e.id === event.id);
    let color = globalIndex !== -1 
      ? (EVENT_COLORS[globalIndex % EVENT_COLORS.length] ?? ROADMAP_INDICATOR_COLOR)
      : ROADMAP_INDICATOR_COLOR;

    if (isPast) {
      color = "#D1D5DB";
    }

    return {
      eventId: event.id,
      color,
      role: isStart && isEnd ? "solo" : isStart ? "start" : isEnd ? "end" : "middle",
    };
  });
}

/**
 * 한 주(Row) 단위로 이벤트들을 슬롯(층)에 배치합니다. (최대 5개 슬롯)
 */
export function packEventsForWeek(row: DayCell[], events: GameEvent[], viewYear: number, viewMonth: number, maxSlots: number = 5) {
  const rowEventIdsSet = new Set<string>();
  row.forEach(cell => {
    const [cy, cm, cd] = getCellDate(cell, viewYear, viewMonth);
    const bars = getCellBarsForDate(events, cy, cm, cd);
    bars.forEach(b => rowEventIdsSet.add(b.eventId));
  });
  
  const uniqueEventsInRow = Array.from(rowEventIdsSet)
    .map(id => events.find(e => e.id === id))
    .filter(Boolean) as GameEvent[];

  uniqueEventsInRow.sort((a, b) => {
    if (a.start !== b.start) return a.start.localeCompare(b.start);
    return b.end.localeCompare(a.end);
  });

  const slots = Array.from({ length: maxSlots }, () => [] as string[]);
  uniqueEventsInRow.forEach(ev => {
    for (let i = 0; i < maxSlots; i++) {
      const slot = slots[i];
      const hasOverlap = slot.some(slotEvId => {
        const slotEv = events.find(e => e.id === slotEvId)!;
        return Math.max(dayjs(ev.start).unix(), dayjs(slotEv.start).unix()) <= Math.min(dayjs(ev.end).unix(), dayjs(slotEv.end).unix());
      });
      if (!hasOverlap) {
        slot.push(ev.id);
        break;
      }
    }
  });

  return slots;
}
